import { useRef, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  useGetPtoProjectQuery,
  useListPtoRdDocumentsQuery,
  useCreatePtoRdDocumentMutation,
  useListPtoRevisionsQuery,
  useUploadPtoRevisionMutation,
  useReindexPtoRevisionMutation,
  useDeletePtoRdDocumentMutation,
  useRestorePtoRdDocumentMutation,
  useDeletePtoRevisionMutation,
  useRestorePtoRevisionMutation,
  type PtoRdDocument,
  type PtoRdRevision,
} from '@shared/api';
import { useAgentPermission, usePtoDeleteAccess } from '@entities/user';
import { afcStampLabel, fmtFieldDate, volumesRelinkWarnings } from '@entities/field';
import { Button } from '@shared/ui/Button';
import { RevisionFlagNotes } from './RevisionFlagsBanner';
import { RdQueuePanel } from './rdImport';
import { Input } from '@shared/ui/Input';
import { deletionDaysLeft, DeletionBadge } from './deletion';
import { FieldOnSitePanel } from './FieldOnSitePanel';
import { RdCarryOverPanel } from './RdCarryOverPanel';
import { RefreshSectionsButton } from './RefreshSectionsButton';

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 360px) 1fr;
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
`;

/* Колонка ревизий «прилипает» к верху области прокрутки: при выборе документа
   внизу длинного списка ревизии и «Объёмы» остаются рядом, без прокрутки вверх
   (замечание инженера ПТО 23.10.2026). Высокая панель прокручивается внутри.
   На узком экране колонки встают друг под другом — там к панели прокручиваем
   при выборе документа (`selectDocument`). */
const RevisionsColumn = styled.div`
  position: sticky;
  top: 0;
  align-self: start;
  max-height: calc(100vh - 140px);
  overflow-y: auto;

  @media (max-width: 880px) {
    position: static;
    max-height: none;
    overflow: visible;
  }
`;

const DocItem = styled.button<{ $active: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primarySoft : theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;
  font: inherit;
  color: inherit;
`;

const DocCode = styled.div`
  font-weight: 600;
`;

const DocMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const RevRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2.5)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

/** Привязки Прораба, которые замена позиций ревизии не сохранила. */
const RelinkNote = styled.div<{ $danger: boolean }>`
  font-size: 12.5px;
  line-height: 1.5;
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  padding-left: ${({ theme }) => theme.spacing(2)};
  border-left: 3px solid
    ${({ $danger, theme }) => ($danger ? theme.colors.status.danger : theme.colors.status.warning)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RelinkHint = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Метка комплекта «в производство: дата, подписант» (штамп заказчика, №6). */
const StampBadge = styled.span`
  display: inline-block;
  margin-left: ${({ theme }) => theme.spacing(1)};
  padding: 0 ${({ theme }) => theme.spacing(1)};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
`;

function ingestLabel(s: string): string {
  switch (s) {
    case 'indexed':
      return 'проиндексировано';
    case 'parsing':
      return 'индексация…';
    case 'failed':
      return 'ошибка';
    default:
      return 'в очереди';
  }
}

const Status = styled.span<{ $s: string }>`
  font-size: 12px;
  font-weight: 600;
  color: ${({ $s, theme }) =>
    $s === 'indexed'
      ? theme.colors.primary
      : $s === 'failed'
        ? 'crimson'
        : theme.colors.text.secondary};
`;

export function PtoProjectView() {
  const { projectId = '' } = useParams();
  const { currentData: projectData } = useGetPtoProjectQuery(projectId, { skip: !projectId });
  const { can_write } = useAgentPermission('pto');
  const canDelete = usePtoDeleteAccess();
  const [showDeleted, setShowDeleted] = useState(false);
  const { currentData: docsData, isFetching: docsLoading, error: docsError } = useListPtoRdDocumentsQuery(
    { projectId, deleted: showDeleted },
    { skip: !projectId },
  );
  const [createDoc, { isLoading: creatingDoc }] = useCreatePtoRdDocumentMutation();
  const [deleteDoc] = useDeletePtoRdDocumentMutation();
  const [restoreDoc] = useRestorePtoRdDocumentMutation();

  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const revisionsRef = useRef<HTMLDivElement>(null);
  const [code, setCode] = useState('');
  const [mark, setMark] = useState('');
  const [title, setTitle] = useState('');
  const [discipline, setDiscipline] = useState('');

  const documents: PtoRdDocument[] = docsData?.items ?? [];
  const selectedDocument = documents.find((document) => document.id === selectedDoc);
  const project = projectData?.project;

  /** Выбрать документ: ревизии нового документа — с начала колонки; на узком
   *  экране (колонки друг под другом) — прокрутить к панели ревизий. */
  function selectDocument(id: string) {
    setSelectedDoc(id);
    const column = revisionsRef.current;
    if (!column) return;
    column.scrollTop = 0;
    if (window.matchMedia('(max-width: 880px)').matches) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      column.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }
  }

  async function onCreateDoc(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim() || !title.trim()) return;
    await createDoc({
      projectId,
      code: code.trim(),
      mark: mark.trim() || undefined,
      title: title.trim(),
      discipline: discipline.trim() || undefined,
    }).unwrap();
    setCode('');
    setMark('');
    setTitle('');
    setDiscipline('');
  }

  return (
    <div>
      <Row>
        <h1 style={{ margin: 0, fontSize: 22 }}>{project?.name ?? 'Проект'}</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to={`/agents/pto/rd-import?projectId=${encodeURIComponent(projectId)}`}>
            <Button $variant="secondary">Пакетная загрузка РД</Button>
          </Link>
          <Link to={`/agents/pto/${projectId}/acts`}>
            <Button $variant="secondary">Акты ИД и реквизиты</Button>
          </Link>
          <Link to={`/agents/pto/${projectId}/kfo`}>
            <Button $variant="secondary">КФО и RFI</Button>
          </Link>
        </div>
      </Row>

      {/* Связка с ОСНОВА.Прораб: площадки объекта, прорабы, конструктив из РД и
          факты объёмов, которые прорабы заявили по проекту. */}
      {projectId && (
        <FieldOnSitePanel
          projectId={projectId}
          projectName={project?.name}
          projectDeleted={!!project?.deleted_at}
        />
      )}

      {/* Новая ревизия РД заменила прежнюю, а привязки Прораба остались на её
          позициях: предпросмотр и перенос на действующую ревизию. */}
      {projectId && (
        <RdCarryOverPanel
          projectId={projectId}
          canWrite={can_write}
          projectDeleted={!!project?.deleted_at}
        />
      )}

      <Columns>
        <div>
          <h3 style={{ marginTop: 0 }}>Документы РД</h3>
          {/* Администратору: «Раздел» и номера строк по основным надписям листов. */}
          <RefreshSectionsButton projectId={projectId} projectDeleted={!!project?.deleted_at} />
          {can_write && (
            <Muted style={{ marginBottom: 8 }}>
              Много комплектов?{' '}
              <Link to={`/agents/pto/rd-import?projectId=${encodeURIComponent(projectId)}`}>
                Загрузите РД архивом
              </Link>{' '}
              — шифр, марку, наименование и титул программа возьмёт из документов сама.
            </Muted>
          )}
          <RdQueuePanel compact />
          {canDelete && (
            <Muted style={{ marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                />
                Показать помеченные на удаление
              </label>
            </Muted>
          )}
          {can_write && (
            <Panel as="form" onSubmit={onCreateDoc} style={{ marginBottom: 16 }}>
              <Field>
                Шифр документа
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="АР-1" />
              </Field>
              <Field>
                Марка (опц.)
                <Input value={mark} onChange={(e) => setMark(e.target.value)} placeholder="АР" />
              </Field>
              <Field>
                Наименование
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Архитектурные решения"
                />
              </Field>
              <Field>
                Дисциплина (опц.)
                <Input
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                  placeholder="Архитектура"
                />
              </Field>
              <Button
                $variant="primary"
                type="submit"
                disabled={creatingDoc || !code.trim() || !title.trim()}
              >
                {creatingDoc ? 'Добавление…' : 'Добавить документ'}
              </Button>
            </Panel>
          )}
          {docsLoading && !docsData ? (
            <Muted>Загрузка…</Muted>
          ) : docsError ? (
            <Muted>Не удалось загрузить документы РД. Повторите попытку.</Muted>
          ) : documents.length === 0 ? (
            <Muted>Документов РД пока нет.</Muted>
          ) : (
            documents.map((d) => (
              <div key={d.id} style={{ marginBottom: 8 }}>
                <DocItem
                  $active={selectedDoc === d.id}
                  onClick={() => selectDocument(d.id)}
                  style={{ marginBottom: 0 }}
                >
                  <DocCode>{d.code}</DocCode>
                  <DocMeta>
                    {d.title}
                    {d.mark ? ` · ${d.mark}` : ''}
                    {d.deleted_at ? (
                      <>
                        {' · '}
                        <DeletionBadge>
                          удаляется · осталось {deletionDaysLeft(d.deleted_at)} дн.
                        </DeletionBadge>
                      </>
                    ) : null}
                  </DocMeta>
                </DocItem>
                {canDelete && (
                  <div style={{ marginTop: 2 }}>
                    {d.deleted_at ? (
                      <Button $variant="ghost" $size="sm" onClick={() => restoreDoc(d.id)}>
                        Восстановить
                      </Button>
                    ) : (
                      <Button
                        $variant="ghost"
                        $size="sm"
                        onClick={() => {
                          if (
                            window.confirm(
                              'Пометить документ РД на удаление? Будет удалён со всеми ревизиями через 30 дней; до этого можно восстановить.',
                            )
                          )
                            deleteDoc(d.id);
                        }}
                      >
                        Удалить
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <RevisionsColumn ref={revisionsRef}>
          <h3 style={{ marginTop: 0 }}>Ревизии</h3>
          {selectedDocument ? (
            <RevisionsPanel
              key={selectedDocument.id}
              projectId={projectId}
              documentId={selectedDocument.id}
              canWrite={can_write}
              canDelete={canDelete}
              showDeleted={showDeleted}
            />
          ) : (
            <Muted>Выберите документ слева, чтобы увидеть его ревизии и загрузить новую.</Muted>
          )}
        </RevisionsColumn>
      </Columns>
    </div>
  );
}

function RevisionsPanel({
  projectId,
  documentId,
  canWrite,
  canDelete,
  showDeleted,
}: {
  projectId: string;
  documentId: string;
  canWrite: boolean;
  canDelete: boolean;
  showDeleted: boolean;
}) {
  const { data, isLoading } = useListPtoRevisionsQuery({ documentId, deleted: showDeleted });
  const [upload, { isLoading: uploading }] = useUploadPtoRevisionMutation();
  const [reindex] = useReindexPtoRevisionMutation();
  const [deleteRevision] = useDeletePtoRevisionMutation();
  const [restoreRevision] = useRestorePtoRevisionMutation();
  const [files, setFiles] = useState<File[]>([]);
  const [revLabel, setRevLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const revisions: PtoRdRevision[] = data?.items ?? [];

  async function onUpload(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (files.length === 0) return;
    const form = new FormData();
    if (revLabel.trim()) form.append('rev_label', revLabel.trim());
    for (const f of files) form.append('file', f);
    try {
      await upload({ projectId, documentId, form }).unwrap();
      setFiles([]);
      setRevLabel('');
    } catch {
      setError('Не удалось загрузить ревизию.');
    }
  }

  return (
    <Panel>
      {canWrite && (
        <form onSubmit={onUpload} style={{ marginBottom: 16 }}>
          <Field>
            Метка ревизии (опц.)
            <Input
              value={revLabel}
              onChange={(e) => setRevLabel(e.target.value)}
              placeholder="Изм.2"
            />
          </Field>
          <input
            type="file"
            multiple
            onChange={(e) => setFiles(e.target.files ? Array.from(e.target.files) : [])}
            style={{ marginBottom: 12 }}
          />
          <div>
            <Button $variant="primary" type="submit" disabled={uploading || files.length === 0}>
              {uploading ? 'Загрузка…' : 'Загрузить ревизию'}
            </Button>
          </div>
          {error && <Muted style={{ color: 'crimson', marginTop: 8 }}>{error}</Muted>}
        </form>
      )}

      {isLoading ? (
        <Muted>Загрузка ревизий…</Muted>
      ) : revisions.length === 0 ? (
        <Muted>Ревизий нет. Загрузите файл РД выше.</Muted>
      ) : (
        revisions.map((r) => (
          <RevRow key={r.id}>
            <div>
              <div style={{ fontWeight: 600 }}>
                Ревизия {r.revision_no}
                {r.rev_label ? ` (${r.rev_label})` : ''}{' '}
                {r.status === 'active' ? '· действующая' : '· заменена'}
                {afcStampLabel(r.metadata) && (
                  <StampBadge title="Штамп заказчика «В ПРОИЗВОДСТВО РАБОТ» на листах ревизии">
                    {afcStampLabel(r.metadata)}
                  </StampBadge>
                )}
              </div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                {r.source_filename} ·{' '}
                <Status $s={r.ingest_status}>{ingestLabel(r.ingest_status)}</Status>
                {r.total_pages ? ` · ${r.total_pages} стр.` : ''}
                {r.deleted_at ? (
                  <>
                    {' · '}
                    <DeletionBadge>
                      удаляется · осталось {deletionDaysLeft(r.deleted_at)} дн.
                    </DeletionBadge>
                  </>
                ) : null}
              </div>
              {/* Полнота последней индексации: OCR обрезан, разбор таблиц упал,
                  позиции не заменены. */}
              {!r.deleted_at && <RevisionFlagNotes revision={r} compact />}
              {/* Итог последней замены позиций (извлечение, переиндексация): что из
                  привязок Прораба не перенеслось на новые позиции. */}
              {!r.deleted_at &&
                volumesRelinkWarnings(r.metadata).map((w) => (
                  <RelinkNote key={`${w.roles}:${w.tone}`} $danger={w.tone === 'danger'}>
                    {w.text}
                    <RelinkHint>
                      {w.source}
                      {w.at ? `, ${fmtFieldDate(w.at)}` : ''}. {w.hint}
                    </RelinkHint>
                  </RelinkNote>
                ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Link to={`/agents/pto/${projectId}/volumes/${r.id}`}>
                <Button $variant="ghost" $size="sm">
                  Объёмы
                </Button>
              </Link>
              {canWrite && !r.deleted_at && (
                <>
                  <Button $variant="ghost" $size="sm" onClick={() => reindex({ id: r.id })}>
                    Переиндексировать
                  </Button>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    title="Распознать текст заново (OCR) — если в позициях «абракадабра» из-за битых шрифтов PDF"
                    onClick={() => reindex({ id: r.id, ocr: true })}
                  >
                    + OCR
                  </Button>
                </>
              )}
              {canDelete &&
                (r.deleted_at ? (
                  <Button $variant="ghost" $size="sm" onClick={() => restoreRevision(r.id)}>
                    Восстановить
                  </Button>
                ) : (
                  <Button
                    $variant="ghost"
                    $size="sm"
                    onClick={() => {
                      if (
                        window.confirm(
                          'Пометить ревизию на удаление? Будет удалена через 30 дней; до этого можно восстановить.',
                        )
                      )
                        deleteRevision(r.id);
                    }}
                  >
                    Удалить
                  </Button>
                ))}
            </div>
          </RevRow>
        ))
      )}
    </Panel>
  );
}
